import { Data, Menu, SubscriptionPlan, UI, Utils } from 'common';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import uniqueId from 'lodash/uniqueId';

import { RoutePath } from './RoutePath';

const { UserRole, Session, BillingPlans } = Utils;
const { ItemType } = Menu;
const { InternalCapability, Capability } = Data;
// constants
export const ACTION_LINK_TYPE = Object.freeze({
	// internal route
	ROUTE: 'ROUTE',

	// external route
	NAVIGATION: 'NAVIGATION',
});

const STORE_LINKS = Object.freeze({
	HISTORY: 'StoreOrderHistory',
	SUBSCRIPTIONS: 'StoreSubscriptions',
	ADDRESSES: 'StoreShippingAddresses',
});

export class LeftMenuItems {
	/**
	 * Build left menu items.
	 * (source: http://wiki/display/RnD/MyAccount+-+full+migration)
	 * (source: http://jira/browse/ATG-4073)
	 * (source: http://jira/browse/ATG-3539)
	 */
	static build(pathName, role, CutAtgStoreLinks, showLabs = false) {
		// Corporate/multi-location hierarchy roles (corporationId !== 0 in
		// Session.getUserRole), as opposed to single-location or plain
		// multi-user (non-corp, corporationId === 0) accounts.
		const isMultiLocationUser = includes(
			[
				UserRole.CorpAdmin,
				UserRole.CorpUser,
				UserRole.RegionAdmin,
				UserRole.LocationAdmin,
			],
			role
		);
		const isUserInCoreOfficeEssentialPlan =
			BillingPlans.isUserInCoreOfficeEssentialPlan();
		const isNewLineupPlan = BillingPlans.isNewLineupPlan();
		const isPayg = BillingPlans.isUserInPaygPlan();
		// expand group when isActive is true
		const isActive = {
			accountSettings: includes(
				[
					RoutePath.cancelAccount,
					RoutePath.changePlan,
					RoutePath.costCodes,
					RoutePath.contactInfo,
					RoutePath.emails,
					RoutePath.paymentSettings,
					RoutePath.paymentMethods,
					RoutePath.shipFromAddress,
					RoutePath.users,
					// Labs only lives here for single-location users.
					...(isMultiLocationUser ? [] : [RoutePath.labs]),
				],
				pathName
			),
			regionSettings: includes(
				[
					RoutePath.allocateFunds,
					RoutePath.contactInfo,
					RoutePath.locations,
					RoutePath.paymentSettings,
					RoutePath.paymentMethods,
					RoutePath.users,
				],
				pathName
			),
			corporateSettings: includes(
				[
					RoutePath.allocateFunds,
					RoutePath.contactInfo,
					RoutePath.locations,
					RoutePath.regions,
					RoutePath.paymentSettings,
					RoutePath.paymentMethods,
					RoutePath.users,
				],
				pathName
			),
			myLocationSettings: includes(
				[
					RoutePath.costCodes,
					RoutePath.emails,
					RoutePath.locationContactInfo,
					RoutePath.locationPaymentMethods,
					RoutePath.locationPaymentSettings,
					RoutePath.locationUsers,
					RoutePath.shipFromAddress,
				],
				pathName
			),
			storeOrders: false,
			profile: includes(
				[
					RoutePath.changePassword,
					RoutePath.commPrefs,
					RoutePath.referAFriend,
					RoutePath.userContactInfo,
					// Labs only lives here for multi-location users.
					...(isMultiLocationUser ? [RoutePath.labs] : []),
				],
				pathName
			),
		};

		const hasManageUsersCapability = Session.hasCapability(
			InternalCapability.ManageUsers
		);
		const isUsersTabVisible =
			isUserInCoreOfficeEssentialPlan ||
			includes([UserRole.MultiAdmin, UserRole.LocationAdmin], role) ||
			(isNewLineupPlan && hasManageUsersCapability) ||
			(includes([UserRole.CorpUser, UserRole.MultiUser], role) &&
				hasManageUsersCapability);

		const isShipFromVisible =
			includes(
				[UserRole.SingleUser, UserRole.MultiAdmin, UserRole.LocationAdmin],
				role
			) ||
			(includes([UserRole.CorpUser, UserRole.MultiUser], role) &&
				Session.hasCapability(InternalCapability.ChangePhysicalAddress));

		const isCostCodesVisible =
			includes(
				[UserRole.SingleUser, UserRole.MultiAdmin, UserRole.LocationAdmin],
				role
			) ||
			(includes([UserRole.CorpUser, UserRole.MultiUser], role) &&
				Session.hasCapability(InternalCapability.EditCostCodes));

		const isPaymentSettingsVisible =
			includes(
				[UserRole.SingleUser, UserRole.MultiAdmin, UserRole.LocationAdmin],
				role
			) ||
			(includes([UserRole.CorpUser, UserRole.MultiUser], role) &&
				Session.hasCapability(InternalCapability.ChangePaymentMethod));

		const isPaymentMethodsVisible =
			includes(
				[UserRole.SingleUser, UserRole.MultiAdmin, UserRole.LocationAdmin],
				role
			) ||
			(includes([UserRole.CorpUser, UserRole.MultiUser], role) &&
				Session.hasCapability(InternalCapability.ChangePaymentMethod));

		// menu items config
		const accountSettingsMenu = {
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Account Settings',
			visible: includes(
				[
					UserRole.SingleUser,
					UserRole.MultiUser,
					UserRole.MultiAdmin,
					UserRole.CorpUser,
					UserRole.LocationAdmin,
				],
				role
			),
			active: isActive.accountSettings, // `true` to highlight the group
			collapsed: !isActive.accountSettings, // `true` to collapse the group
			items: [
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Users',
					visible: isUsersTabVisible,
					active: pathName === RoutePath.users,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'meterUsers',
						routePath: RoutePath.users,
					},
					dataqa: 'account-left-menu-users',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Ship From Address',
					visible: isShipFromVisible,
					active: pathName === RoutePath.shipFromAddress,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'lpo',
						routePath: RoutePath.shipFromAddress,
					},
					dataqa: 'account-left-menu-shipfrom-address',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Cost Codes',
					visible: isCostCodesVisible,
					showFeatureGate: isPayg,
					active: pathName === RoutePath.costCodes,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'costCode',
						routePath: RoutePath.costCodes,
					},
					dataqa: 'account-left-menu-cost-codes',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Settings',
					visible: isPaymentSettingsVisible,
					active: pathName === RoutePath.paymentSettings,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.paymentSettings,
						navigationToken: 'defaultPaymentMethods',
					},
					dataqa: 'account-left-menu-payment-settings',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Methods',
					visible: isPaymentMethodsVisible,
					active: pathName === RoutePath.paymentMethods,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.paymentMethods,
						navigationToken: 'paymentMethod',
					},
					dataqa: 'account-left-menu-payment-methods',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Contact Info',
					visible: includes(
						[UserRole.SingleUser, UserRole.MultiAdmin, UserRole.LocationAdmin],
						role
					),
					active: pathName === RoutePath.contactInfo,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'meterContactInfo',
						routePath: RoutePath.contactInfo,
					},
					dataqa: 'account-left-menu-contact-info',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Subscription Plan',
					// Hide the tab for non-admin users without permission to change
					// the service plan.
					visible:
						!includes(
							[UserRole.SingleUser, UserRole.MultiUser, UserRole.CorpUser],
							role
						) || Session.hasCapability(Capability.ChangeServicePlan),
					active:
						pathName === RoutePath.changePlan ||
						pathName === RoutePath.cancelAccount,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'changePlan',
						routePath: RoutePath.changePlan,
					},
					dataqa: 'account-left-menu-subscription-plan',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Tracking Email',
					visible: includes(
						[UserRole.SingleUser, UserRole.MultiAdmin, UserRole.LocationAdmin],
						role
					),
					active: pathName === RoutePath.emails,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'customShipNotifyEmail',
						routePath: RoutePath.emails,
					},
					dataqa: 'account-left-menu-tracking-email',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Labs',
					// Labs moves to the Profile section for multi-location users.
					visible: showLabs && !isMultiLocationUser,
					active: pathName === RoutePath.labs,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'labs',
						routePath: RoutePath.labs,
					},
					dataqa: 'account-left-menu-labs',
				},
			],
		};

		const corporateSettingsMenu = {
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Corporate-Wide Settings',
			visible: role === UserRole.CorpAdmin,
			active: isActive.corporateSettings,
			collapsed: !isActive.corporateSettings,
			items: [
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Regions',
					visible: true,
					active: pathName === RoutePath.regions,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'regions',
						routePath: RoutePath.regions,
					},
					dataqa: 'account-left-menu-corporate-regions',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'All Locations',
					visible: true,
					active: pathName === RoutePath.locations,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'meters',
						routePath: RoutePath.locations,
					},
					dataqa: 'account-left-menu-corporate-all-locations',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'All Users',
					visible: true,
					active: pathName === RoutePath.users,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'corpUsers',
						routePath: RoutePath.users,
					},
					dataqa: 'account-left-menu-corporate-all-users',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Settings',
					visible: true,
					active: pathName === RoutePath.paymentSettings,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.paymentSettings,
						navigationToken: 'corpPayment',
					},
					dataqa: 'account-left-menu-corporate-payment-settings',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Methods',
					visible: true,
					active: pathName === RoutePath.paymentMethods,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.paymentMethods,
						navigationToken: 'paymentMethod',
					},
					dataqa: 'account-left-menu-corporate-payment-methods',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Allocate Funds',
					visible: true,
					active: pathName === RoutePath.allocateFunds,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'addFunds',
						routePath: RoutePath.allocateFunds,
					},
					dataqa: 'account-left-menu-corporate-allocate-funds',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Contact Info',
					visible: true,
					active: pathName === RoutePath.contactInfo,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'corpContactInfo',
						routePath: RoutePath.contactInfo,
					},
					dataqa: 'account-left-menu-corporate-contact-info',
				},
			],
		};

		const regionSettingsMenu = {
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Region-Wide Settings',
			visible: role === UserRole.RegionAdmin,
			active: isActive.regionSettings, // `true` to highlight the group
			collapsed: !isActive.regionSettings,
			items: [
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'All Locations',
					visible: true,
					active: pathName === RoutePath.locations,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'meters',
						routePath: RoutePath.locations,
					},
					dataqa: 'account-left-menu-region-all-locations',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'All Users',
					visible: true,
					active: pathName === RoutePath.users,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'corpUsers',
						routePath: RoutePath.users,
					},
					dataqa: 'account-left-menu-region-all-users',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Settings',
					visible: true,
					active: pathName === RoutePath.paymentSettings,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.paymentSettings,
						navigationToken: 'regionDefaultPayment',
					},
					dataqa: 'account-left-menu-region-payment-settings',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Methods',
					visible: true,
					active: pathName === RoutePath.paymentMethods,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.paymentMethods,
						navigationToken: 'paymentMethod',
					},
					dataqa: 'account-left-menu-region-payment-methods',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Allocate Funds',
					visible: true,
					active: pathName === RoutePath.allocateFunds,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'addFunds',
						routePath: RoutePath.allocateFunds,
					},
					dataqa: 'account-left-menu-region-allocate-funds',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Contact Info',
					visible: true,
					active: pathName === RoutePath.contactInfo,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'corpContactInfo',
						routePath: RoutePath.contactInfo,
					},
					dataqa: 'account-left-menu-region-contact-info',
				},
			],
		};

		const myLocationSettingsMenu = {
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'My Location Settings',
			visible: includes([UserRole.CorpAdmin, UserRole.RegionAdmin], role),
			active: isActive.myLocationSettings, // `true` to highlight the group
			collapsed: !isActive.myLocationSettings,
			items: [
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Users',
					visible: true,
					active: pathName === RoutePath.locationUsers,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'corpUsers',
						routePath: RoutePath.locationUsers,
					},
					dataqa: 'account-left-menu-my-location-users',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Ship From Address',
					visible: true,
					active: pathName === RoutePath.shipFromAddress,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'lpo',
						routePath: RoutePath.shipFromAddress,
					},
					dataqa: 'account-left-menu-my-location-shipfrom-address',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Cost Codes',
					visible: true,
					showFeatureGate: isPayg,
					active: pathName === RoutePath.costCodes,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'costCode',
						routePath: RoutePath.costCodes,
					},
					dataqa: 'account-left-menu-my-location-cost-codes',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Settings',
					visible: true,
					active: pathName === RoutePath.locationPaymentSettings,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.locationPaymentSettings,
						navigationToken: UserRole.CorpAdmin
							? 'corpPayment'
							: 'regionDefaultPayment',
					},
					dataqa: 'account-left-menu-my-location-payment-settings',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Payment Methods',
					visible: true,
					active: pathName === RoutePath.locationPaymentMethods,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						routePath: RoutePath.locationPaymentMethods,
						navigationToken: 'paymentMethod',
					},
					dataqa: 'account-left-menu-my-location-payment-methods',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Contact Info',
					visible: true,
					active: pathName === RoutePath.locationContactInfo,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'corpContactInfo',
						routePath: RoutePath.locationContactInfo,
					},
					dataqa: 'account-left-menu-my-location-contact-info',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Tracking Email',
					visible: true,
					active: pathName === RoutePath.emails,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'customShipNotifyEmail',
						routePath: RoutePath.emails,
					},
					dataqa: 'account-left-menu-my-location-tracking-email',
				},
			],
		};

		const storeOrdersMenu = {
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Store Orders',
			visible: !Session.canAccessPilotSuppliesStore(),
			active: isActive.storeOrders, // `true` to highlight the group
			collapsed: !isActive.storeOrders, // `true` to collapse the group
			items: [
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Order History',
					visible: true,
					active: false,
					data: {
						type: ACTION_LINK_TYPE.NAVIGATION,
						navigationToken: CutAtgStoreLinks ? STORE_LINKS.HISTORY : 'history',
					},
					dataqa: 'account-left-menu-store-orders-order-history',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Store Credits',
					visible: CutAtgStoreLinks !== true,
					active: false,
					data: {
						type: ACTION_LINK_TYPE.NAVIGATION,
						navigationToken: 'giftCertBalance',
					},
					dataqa: 'account-left-menu-store-orders-store-credits',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Reorder',
					visible: CutAtgStoreLinks !== true,
					active: false,
					data: {
						type: ACTION_LINK_TYPE.NAVIGATION,
						navigationToken: 'reorder',
					},
					dataqa: 'account-left-menu-store-orders-reorder',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Shipping Addresses',
					// not visible for 'MultiUser' nor 'CorpUser' role
					visible: !includes([UserRole.MultiUser, UserRole.CorpUser], role),
					active: false,
					data: {
						type: ACTION_LINK_TYPE.NAVIGATION,
						navigationToken: CutAtgStoreLinks
							? STORE_LINKS.ADDRESSES
							: 'shippingAddresses',
					},
					dataqa: 'account-left-menu-store-orders-shipping-addresses',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Manage Auto-Reorders',
					visible: true,
					active: false,
					data: {
						type: ACTION_LINK_TYPE.NAVIGATION,
						navigationToken: CutAtgStoreLinks
							? STORE_LINKS.SUBSCRIPTIONS
							: 'autoReorder',
					},
					dataqa: 'account-left-menu-store-orders-manage-auto-reorders',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Image Gallery',
					visible: CutAtgStoreLinks !== true,
					active: false,
					data: {
						type: ACTION_LINK_TYPE.NAVIGATION,
						navigationToken: 'imageGallery',
					},
					dataqa: 'account-left-menu-store-orders-image-gallery',
				},
			],
		};

		const profileMenu = {
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Profile',
			visible: true,
			active: isActive.profile, // `true` to highlight the group
			collapsed: !isActive.profile, // `true` to collapse the group
			items: [
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Personal Contact Info',
					visible: true,
					active: pathName === RoutePath.userContactInfo,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'usercontactinfo',
						routePath: RoutePath.userContactInfo,
					},
					dataqa: 'account-left-menu-profile-personal-contact-info',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Change Password',
					visible: true,
					active: pathName === RoutePath.changePassword,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'changePassword',
						routePath: RoutePath.changePassword,
					},
					dataqa: 'account-left-menu-profile-change-password',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Comm Prefs',
					visible: false,
					active: pathName === RoutePath.commPrefs,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'commPreferences',
						routePath: RoutePath.commPrefs,
					},
					dataqa: 'account-left-menu-profile-comm-prefs',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Refer a Friend',
					visible: true,
					active: pathName === RoutePath.referAFriend,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'referAFriend',
						routePath: RoutePath.referAFriend,
					},
					dataqa: 'account-left-menu-profile-refer-a-friend',
				},
				{
					id: parseInt(uniqueId(), 10),
					type: ItemType.Badge,
					title: 'Labs',
					// Labs lives in Profile for multi-location users only.
					visible: showLabs && isMultiLocationUser,
					active: pathName === RoutePath.labs,
					data: {
						type: ACTION_LINK_TYPE.ROUTE,
						navigationToken: 'labs',
						routePath: RoutePath.labs,
					},
					dataqa: 'account-left-menu-profile-labs',
				},
			],
		};

		const items = [
			accountSettingsMenu,
			corporateSettingsMenu,
			regionSettingsMenu,
			myLocationSettingsMenu,
			storeOrdersMenu,
			profileMenu,
		];

		return filter(items, item => {
			// return the 1st-level group item that should be visible
			if (item.visible === true) {
				// return the 2nd-level child items that should be visible
				item.items = filter(item.items, { visible: true });

				return item;
			}
		});
	}
}
